import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Pencil, Plus } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Particles } from "@/components/catalog/Particles";
import { Spinner } from "@/components/catalog/TitleGrid";
import { ProfileEditor } from "@/components/profiles/ProfileEditor";
import { useProfiles } from "@/context/ProfileContext";
import { useBilling } from "@/context/BillingContext";

export default function Profiles() {
  const { profiles, selectProfile, loadProfiles } = useProfiles();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const manage = params.get("manage") === "1";
  const [editor, setEditor] = useState({ open: false, profile: null });
  const { ent } = useBilling();
  const MAX = ent?.max_profiles ?? 2;

  const pick = (p) => {
    if (manage) return setEditor({ open: true, profile: p });
    const back = sessionStorage.getItem("aetryx_return");
    sessionStorage.removeItem("aetryx_return");
    selectProfile(p.id);
    navigate(back || "/");
  };
  // Signed in from the guest modal with a single profile: skip the picker and go straight back.
  useEffect(() => {
    if (!manage && profiles?.length === 1 && sessionStorage.getItem("aetryx_return")) pick(profiles[0]);
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profiles) return <div className="min-h-screen bg-[#050505]"><Spinner /></div>;

  return (
    <div className="relative min-h-screen bg-[#050505] text-white grain overflow-hidden" data-testid="profiles-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.14),transparent_60%)]" />
      <Particles count={18} />
      <div className="relative z-10 px-5 sm:px-10 pt-8"><Logo /></div>
      <div className="relative z-10 mx-auto flex max-w-5xl flex-col items-center px-5 pt-16 sm:pt-24 pb-16 text-center">
        <p className="eyebrow mb-4">{manage ? "Manage profiles" : "AETRYX AI Studios"}</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl gold-text">{manage ? "Manage Profiles" : "Who's watching?"}</h1>
        <div className="mt-14 flex flex-wrap justify-center gap-6 sm:gap-10">
          {profiles.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              data-testid={`profile-tile-${p.id}`}
              onClick={() => pick(p)}
              className="group flex w-[110px] sm:w-[140px] flex-col items-center gap-3"
            >
              <span className="relative block overflow-hidden rounded-2xl ring-1 ring-[#D4AF37]/30 transition-[box-shadow,transform] duration-300 group-hover:ring-2 group-hover:ring-[#FFD86A] group-hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] group-hover:-translate-y-1">
                <img src={p.avatar} alt={p.name} className="h-[110px] w-[110px] sm:h-[140px] sm:w-[140px] object-cover" />
                {manage && <span className="absolute inset-0 grid place-items-center bg-black/55"><Pencil className="h-8 w-8 text-[#FFD86A]" /></span>}
                {p.is_kids && <span className="absolute bottom-2 left-2 rounded bg-[#D4AF37] px-1.5 py-0.5 text-[0.62rem] font-extrabold text-black">KIDS</span>}
              </span>
              <span className="text-base font-semibold text-white/80 group-hover:text-[#FFD86A] transition-colors truncate max-w-full">{p.name}</span>
              <span data-testid={`profile-role-${p.id}`} className="-mt-2 text-[0.62rem] font-bold uppercase tracking-[0.25em] text-[#D4AF37]/70">{p.is_kids ? "Kids" : i === 0 ? (ent?.create_studio ? "Owner · Creator" : "Owner") : "Viewer"}</span>
            </motion.button>
          ))}
          {profiles.length < MAX && (
            <button data-testid="add-profile-btn" onClick={() => setEditor({ open: true, profile: null })} className="group flex w-[110px] sm:w-[140px] flex-col items-center gap-3">
              <span className="grid h-[110px] w-[110px] sm:h-[140px] sm:w-[140px] place-items-center rounded-2xl border border-dashed border-[#D4AF37]/40 text-[#D4AF37] transition-colors group-hover:border-[#FFD86A] group-hover:bg-[#D4AF37]/10">
                <Plus className="h-10 w-10" />
              </span>
              <span className="text-base font-semibold text-white/60 group-hover:text-[#FFD86A] transition-colors">Add Profile</span>
            </button>
          )}
        </div>
        {profiles.length >= MAX && MAX < 5 && <p data-testid="profile-limit-note" className="mt-10 text-sm text-[#8B8B8B]">Your {ent?.plan_name} plan includes {MAX} profiles. <button onClick={() => navigate("/pricing")} className="text-[#FFD86A] hover:underline">Upgrade for up to 5</button></p>}
        <button
          data-testid="manage-profiles-toggle"
          onClick={() => setParams(manage ? {} : { manage: "1" })}
          className={`mt-16 px-8 py-3 text-sm tracking-[0.2em] uppercase ${manage ? "btn-gold" : "btn-ghost-gold"}`}
        >
          {manage ? "Done" : "Manage Profiles"}
        </button>
      </div>
      <ProfileEditor open={editor.open} profile={editor.profile} onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))} onSaved={loadProfiles} />
    </div>
  );
}
